import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Video, MapPin, CalendarDays, CheckCircle2, Loader2, Heart, Users, Film, ClipboardList, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WARDS = ['Ward 1', 'Ward 2', 'Ward 3', 'Ward 4', 'Ward 5', 'Ward 6', 'Ward 7', 'Ward 8'];
const AGE_GROUPS = ['Infants', 'Toddlers', 'Preschool-age children', 'School-age children'];
const HEARD = ['DCELC email', 'Social media', 'Flyer or QR code', 'Partner organization', 'Colleague', 'Other'];

const initialForm = {
  first_name: '', last_name: '', role_title: '', email: '', phone: '',
  preferred_contact: 'Email', preferred_language: '',
  program_name: '', program_type: '', address1: '', address2: '', city: 'Washington', state: 'DC',
  zip_code: '', ward: '', licensing_status: '', licensing_other: '', website: '', years_in_operation: '',
  age_groups: [], languages_used: '',
  story_special: '', story_families: '', story_highlight: '',
  participation_willing: '', scheduling_preferences: '', access_needs: '', how_heard: '',
  ack_no_guarantee: false, ack_contact: false, ack_filming: false, keep_on_file: false,
  website_url: ''
};

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-teal-600";
const labelCls = "block text-sm font-semibold text-slate-700 mb-1.5";

const Field = ({ id, label, required, error, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}{required && <span className="text-rose-600" aria-hidden="true"> *</span>}</label>
    {children}
    {error && <p className="text-xs text-rose-600 mt-1" role="alert">{error}</p>}
  </div>
);

const SectionCard = ({ number, title, children }) => (
  <fieldset className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
    <legend className="sr-only">{title}</legend>
    <div className="flex items-center gap-3 mb-6">
      <span className="w-8 h-8 rounded-full bg-[#0A2037] text-lime-300 flex items-center justify-center text-sm font-bold" aria-hidden="true">{number}</span>
      <h3 className="text-lg font-bold text-slate-800">{title}</h3>
    </div>
    {children}
  </fieldset>
);

const StoryField = ({ id, label, required, value, onChange, error }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}{required && <span className="text-rose-600" aria-hidden="true"> *</span>}</label>
    <textarea id={id} rows={4} maxLength={1500} value={value} onChange={onChange} data-testid={`sb-${id}`}
      className={inputCls} aria-describedby={`${id}-count`} />
    <p id={`${id}-count`} className={`text-xs mt-1 ${value.length > 1400 ? 'text-amber-600' : 'text-slate-400'}`}>{value.length.toLocaleString()} / 1,500 characters</p>
    {error && <p className="text-xs text-rose-600 mt-1" role="alert">{error}</p>}
  </div>
);

export default function StrongBeginnings() {
  const [settings, setSettings] = useState({ deadline: '2026-11-20', open: true });
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [reference, setReference] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/strong-beginnings/settings`).then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.checked }));
  const toggleAge = (g) => setForm(f => ({ ...f, age_groups: f.age_groups.includes(g) ? f.age_groups.filter(x => x !== g) : [...f.age_groups, g] }));

  const scrollToForm = useCallback(() => {
    document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const validate = () => {
    const er = {};
    const req = ['first_name', 'last_name', 'role_title', 'email', 'phone', 'program_name', 'address1', 'zip_code', 'story_special', 'story_families'];
    req.forEach(k => { if (!form[k].trim()) er[k] = 'This field is required.'; });
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) er.email = 'Please enter a valid email address.';
    if (!form.program_type) er.program_type = 'Please choose a program type.';
    if (!form.ward) er.ward = 'Please select your ward.';
    if (!form.licensing_status) er.licensing_status = 'Please select a licensing status.';
    if (form.age_groups.length === 0) er.age_groups = 'Select at least one age group.';
    if (!form.participation_willing) er.participation_willing = 'Please answer this question.';
    if (!form.ack_no_guarantee || !form.ack_contact || !form.ack_filming) er.acks = 'Please check all three required acknowledgments.';
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError('');
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) {
      setTimeout(() => {
        document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/strong-beginnings/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Something went wrong — please try again.');
      setReference(d.reference);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF5]">
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section className="bg-[#0A2037] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/20 text-teal-200 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-6">
              <Video className="w-4 h-4" aria-hidden="true" /> Video Storytelling Series
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
              STRONG <span className="text-lime-300">BEGINNINGS</span>
            </h1>
            <p className="text-lg md:text-xl text-teal-100 font-semibold mb-4">Your program. Your story. DC's strong beginnings.</p>
            <p className="text-slate-300 max-w-2xl leading-relaxed mb-8">
              DCELC is inviting child care programs across all eight wards to share what makes their programs special in a professionally produced video storytelling series.
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-3 mb-10 text-sm">
              <span className="inline-flex items-center gap-2 text-lime-200 font-bold"><Star className="w-4 h-4" aria-hidden="true" /> 16 programs. All 8 wards.</span>
              <span className="inline-flex items-center gap-2 text-slate-200"><MapPin className="w-4 h-4 text-teal-300" aria-hidden="true" /> One center-based and one home-based program from each ward.</span>
              <span className="inline-flex items-center gap-2 text-slate-200"><CalendarDays className="w-4 h-4 text-teal-300" aria-hidden="true" /> {settings.open
                ? `Applications close ${new Date(`${settings.deadline}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`
                : 'Applications are now closed.'}</span>
            </div>
            {settings.open && !reference && (
              <button onClick={scrollToForm} data-testid="sb-hero-apply-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-lime-400 hover:bg-lime-300 text-[#0A2037] font-bold rounded-full text-base transition-colors focus:outline-none focus:ring-4 focus:ring-lime-300/50">
                Apply to Be Featured
              </button>
            )}
          </div>
        </section>

        {/* Success state */}
        {reference && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
            <div className="bg-white border-2 border-emerald-200 rounded-3xl p-10 text-center shadow-sm" data-testid="sb-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Thank you for sharing your story!</h2>
              <p className="text-slate-600 mb-6">Your Strong Beginnings application has been received. DCELC will review applications and contact you about next steps. Submission does not guarantee selection.</p>
              <p className="text-sm text-slate-500">Your application reference number:</p>
              <p className="text-xl font-mono font-bold text-teal-700 mt-1" data-testid="sb-reference">{reference}</p>
              <p className="text-xs text-slate-400 mt-6">A confirmation email is on its way. Questions? Contact admin@dcelc.org.</p>
            </div>
          </section>
        )}

        {!reference && (
          <>
            {/* Info sections */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                <Heart className="w-8 h-8 text-rose-500 mb-4" aria-hidden="true" />
                <h2 className="text-xl font-bold text-slate-800 mb-3">About the Series</h2>
                <p className="text-slate-600 leading-relaxed mb-3">Strong Beginnings celebrates the quality, diversity, and impact of early childhood education across Washington, DC. Part of DCELC's Stronger Together Initiative and ECE as a Public Good campaign, the series will spotlight the educators, environments, and relationships that help young children thrive.</p>
                <p className="text-slate-600 leading-relaxed">We want to hear about your journey, your approach to learning and caregiving, your connection to families, and the cultural traditions, languages, and experiences that make your program unique.</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                <Film className="w-8 h-8 text-teal-600 mb-4" aria-hidden="true" />
                <h2 className="text-xl font-bold text-slate-800 mb-3">What Selected Programs Receive</h2>
                <p className="text-slate-600 leading-relaxed mb-3">Selected programs will receive a professionally produced 90-second to 3-minute video showcasing their program. Programs can share their video through:</p>
                <ul className="text-slate-600 space-y-1.5 list-disc pl-5 mb-3">
                  <li>Family recruitment.</li>
                  <li>Social media.</li>
                  <li>Their website.</li>
                  <li>Community outreach and presentations.</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">Footage will also contribute to a DC-wide compilation highlighting center-based and home-based child care across all eight wards.</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                <Users className="w-8 h-8 text-indigo-600 mb-4" aria-hidden="true" />
                <h2 className="text-xl font-bold text-slate-800 mb-3">Who Should Apply</h2>
                <p className="text-slate-600 leading-relaxed mb-3">DC center-based and home-based child care programs interested in sharing their stories are encouraged to apply.</p>
                <p className="text-slate-600 leading-relaxed mb-3">DCELC welcomes programs representing different neighborhoods, cultures, languages, educational approaches, age groups, program sizes, and years of experience.</p>
                <p className="text-slate-600 leading-relaxed">No previous experience being filmed is needed. DCELC and the production team will help selected programs prepare.</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                <ClipboardList className="w-8 h-8 text-amber-600 mb-4" aria-hidden="true" />
                <h2 className="text-xl font-bold text-slate-800 mb-3">What Participation Involves</h2>
                <ul className="text-slate-600 space-y-1.5 list-disc pl-5 mb-5">
                  <li>A brief planning conversation before filming.</li>
                  <li>An on-camera interview about your program.</li>
                  <li>Filming in approved areas of your child care setting.</li>
                  <li>Coordination of appropriate permissions for anyone appearing on camera.</li>
                  <li>Scheduling with DCELC and the production team to minimize disruption to the program day.</li>
                </ul>
                <h3 className="text-base font-bold text-slate-800 mb-2">Selection Information</h3>
                <p className="text-slate-600 text-sm leading-relaxed">Submitting an application does not guarantee selection. DCELC plans to select 16 programs, including one center-based and one home-based program from each ward. Applicants will be contacted using the information provided in their application.</p>
              </div>
            </section>

            {/* Application form / closed state */}
            <section id="application-form" className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 scroll-mt-24">
              {!settings.open ? (
                <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center shadow-sm" data-testid="sb-closed">
                  <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" aria-hidden="true" />
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">Applications are now closed.</h2>
                  <p className="text-slate-600">Thank you for your interest in Strong Beginnings. For questions, contact <a href="mailto:admin@dcelc.org" className="text-teal-700 font-semibold underline">admin@dcelc.org</a>.</p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="space-y-8" data-testid="sb-application-form">
                  <div className="text-center mb-2">
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Apply to Share Your Program's Story</h2>
                    <p className="text-slate-600">Tell us a little about your program and what makes it special. Brief responses are welcome.</p>
                  </div>

                  <SectionCard number="1" title="Contact Information">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field id="sb-first" label="Primary contact first name" required error={errors.first_name}>
                        <input id="sb-first" className={inputCls} value={form.first_name} onChange={set('first_name')} data-testid="sb-first-name" autoComplete="given-name" />
                      </Field>
                      <Field id="sb-last" label="Primary contact last name" required error={errors.last_name}>
                        <input id="sb-last" className={inputCls} value={form.last_name} onChange={set('last_name')} data-testid="sb-last-name" autoComplete="family-name" />
                      </Field>
                      <Field id="sb-role" label="Role or title" required error={errors.role_title}>
                        <input id="sb-role" className={inputCls} value={form.role_title} onChange={set('role_title')} data-testid="sb-role-title" />
                      </Field>
                      <Field id="sb-email" label="Email address" required error={errors.email}>
                        <input id="sb-email" type="email" className={inputCls} value={form.email} onChange={set('email')} data-testid="sb-email" autoComplete="email" />
                      </Field>
                      <Field id="sb-phone" label="Phone number" required error={errors.phone}>
                        <input id="sb-phone" type="tel" className={inputCls} value={form.phone} onChange={set('phone')} data-testid="sb-phone" autoComplete="tel" />
                      </Field>
                      <Field id="sb-contact-method" label="Preferred contact method">
                        <select id="sb-contact-method" className={inputCls} value={form.preferred_contact} onChange={set('preferred_contact')} data-testid="sb-preferred-contact">
                          {['Email', 'Phone', 'Either'].map(o => <option key={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field id="sb-language" label="Preferred language for communication (optional)">
                        <input id="sb-language" className={inputCls} value={form.preferred_language} onChange={set('preferred_language')} placeholder="e.g., English, Spanish, Amharic…" data-testid="sb-preferred-language" />
                      </Field>
                    </div>
                    {/* Honeypot — intentionally hidden from people and screen readers */}
                    <div className="absolute w-px h-px overflow-hidden" style={{ clip: 'rect(0 0 0 0)' }} aria-hidden="true">
                      <label htmlFor="sb-website-url">Leave this field empty</label>
                      <input id="sb-website-url" tabIndex={-1} autoComplete="off" value={form.website_url} onChange={set('website_url')} />
                    </div>
                  </SectionCard>

                  <SectionCard number="2" title="Program Information">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field id="sb-program-name" label="Program name" required error={errors.program_name}>
                        <input id="sb-program-name" className={inputCls} value={form.program_name} onChange={set('program_name')} data-testid="sb-program-name" />
                      </Field>
                      <Field id="sb-program-type" label="Program type" required error={errors.program_type}>
                        <select id="sb-program-type" className={inputCls} value={form.program_type} onChange={set('program_type')} data-testid="sb-program-type">
                          <option value="">Select…</option>
                          <option>Center-based child care</option>
                          <option>Home-based child care</option>
                        </select>
                      </Field>
                      <Field id="sb-address1" label="Program street address" required error={errors.address1}>
                        <input id="sb-address1" className={inputCls} value={form.address1} onChange={set('address1')} data-testid="sb-address1" autoComplete="street-address" />
                      </Field>
                      <Field id="sb-address2" label="Address line 2 (optional)">
                        <input id="sb-address2" className={inputCls} value={form.address2} onChange={set('address2')} data-testid="sb-address2" />
                      </Field>
                      <Field id="sb-city" label="City">
                        <input id="sb-city" className={inputCls} value={form.city} onChange={set('city')} data-testid="sb-city" />
                      </Field>
                      <Field id="sb-state" label="State / District">
                        <input id="sb-state" className={inputCls} value={form.state} onChange={set('state')} data-testid="sb-state" />
                      </Field>
                      <Field id="sb-zip" label="ZIP code" required error={errors.zip_code}>
                        <input id="sb-zip" inputMode="numeric" className={inputCls} value={form.zip_code} onChange={set('zip_code')} data-testid="sb-zip" autoComplete="postal-code" />
                      </Field>
                      <Field id="sb-ward" label="Ward" required error={errors.ward}>
                        <select id="sb-ward" className={inputCls} value={form.ward} onChange={set('ward')} data-testid="sb-ward">
                          <option value="">Select your ward…</option>
                          {WARDS.map(w => <option key={w}>{w}</option>)}
                        </select>
                      </Field>
                      <Field id="sb-licensing" label="Licensing status" required error={errors.licensing_status}>
                        <select id="sb-licensing" className={inputCls} value={form.licensing_status} onChange={set('licensing_status')} data-testid="sb-licensing">
                          <option value="">Select…</option>
                          <option>Currently licensed</option>
                          <option>Licensing in progress</option>
                          <option>Other</option>
                        </select>
                      </Field>
                      {form.licensing_status === 'Other' && (
                        <Field id="sb-licensing-other" label="Please explain">
                          <input id="sb-licensing-other" className={inputCls} value={form.licensing_other} onChange={set('licensing_other')} data-testid="sb-licensing-other" />
                        </Field>
                      )}
                      <Field id="sb-website" label="Website or social media link (optional)">
                        <input id="sb-website" className={inputCls} value={form.website} onChange={set('website')} data-testid="sb-website" />
                      </Field>
                      <Field id="sb-years" label="Years in operation (optional)">
                        <input id="sb-years" className={inputCls} value={form.years_in_operation} onChange={set('years_in_operation')} data-testid="sb-years" />
                      </Field>
                    </div>
                    <div className="mt-5">
                      <p className={labelCls}>Age groups served <span className="text-rose-600" aria-hidden="true">*</span></p>
                      <div className="flex flex-wrap gap-2" role="group" aria-label="Age groups served">
                        {AGE_GROUPS.map(g => (
                          <label key={g} className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${form.age_groups.includes(g) ? 'border-teal-500 bg-teal-50 text-teal-900' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'}`}>
                            <input type="checkbox" className="h-4 w-4" checked={form.age_groups.includes(g)} onChange={() => toggleAge(g)} data-testid={`sb-age-${g.split('-')[0].toLowerCase()}`} />
                            {g}
                          </label>
                        ))}
                      </div>
                      {errors.age_groups && <p className="text-xs text-rose-600 mt-1" role="alert">{errors.age_groups}</p>}
                    </div>
                    <div className="mt-5">
                      <Field id="sb-languages" label="Languages used in your program (optional)">
                        <input id="sb-languages" className={inputCls} value={form.languages_used} onChange={set('languages_used')} data-testid="sb-languages-used" />
                      </Field>
                    </div>
                  </SectionCard>

                  <SectionCard number="3" title="Your Program's Story">
                    <div className="space-y-6">
                      <StoryField id="story_special" label="What makes your child care program special?" required value={form.story_special} onChange={set('story_special')} error={errors.story_special} />
                      <StoryField id="story_families" label="What would you like families and the DC community to know about your program?" required value={form.story_families} onChange={set('story_families')} error={errors.story_families} />
                      <StoryField id="story_highlight" label="Are there cultural traditions, languages, activities, teaching approaches, or community connections you would like to highlight? (optional)" value={form.story_highlight} onChange={set('story_highlight')} />
                      <p className="text-xs text-slate-500">Polished writing isn't required, and there's nothing to attach or upload — your words are enough.</p>
                    </div>
                  </SectionCard>

                  <SectionCard number="4" title="Participation & Access Needs">
                    <div className="space-y-5">
                      <div>
                        <p className={labelCls}>If selected, are you willing to participate in a planning conversation, an on-camera interview, and filming at your program? <span className="text-rose-600" aria-hidden="true">*</span></p>
                        <div className="flex flex-col sm:flex-row gap-2" role="radiogroup" aria-label="Participation willingness">
                          {[['yes', 'Yes'], ['discuss', 'I would like to discuss what participation involves']].map(([v, l]) => (
                            <label key={v} className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${form.participation_willing === v ? 'border-teal-500 bg-teal-50 text-teal-900' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'}`}>
                              <input type="radio" name="participation" className="h-4 w-4" checked={form.participation_willing === v} onChange={() => setForm(f => ({ ...f, participation_willing: v }))} data-testid={`sb-participation-${v}`} />
                              {l}
                            </label>
                          ))}
                        </div>
                        {errors.participation_willing && <p className="text-xs text-rose-600 mt-1" role="alert">{errors.participation_willing}</p>}
                      </div>
                      <Field id="sb-scheduling" label="What days or times generally work best for a planning conversation or filming? (optional)">
                        <input id="sb-scheduling" className={inputCls} value={form.scheduling_preferences} onChange={set('scheduling_preferences')} data-testid="sb-scheduling" />
                      </Field>
                      <Field id="sb-access" label="Do you need language support, accessibility accommodations, or other assistance to participate? (optional)">
                        <input id="sb-access" className={inputCls} value={form.access_needs} onChange={set('access_needs')} data-testid="sb-access-needs" />
                      </Field>
                      <Field id="sb-heard" label="How did you hear about Strong Beginnings? (optional)">
                        <select id="sb-heard" className={inputCls} value={form.how_heard} onChange={set('how_heard')} data-testid="sb-how-heard">
                          <option value="">Select…</option>
                          {HEARD.map(o => <option key={o}>{o}</option>)}
                        </select>
                      </Field>
                    </div>
                  </SectionCard>

                  <SectionCard number="5" title="Acknowledgments">
                    <div className="space-y-3">
                      {[['ack_no_guarantee', 'I understand that submitting this application does not guarantee selection.'],
                        ['ack_contact', 'I authorize DCELC to contact me about this application and the Strong Beginnings video series.'],
                        ['ack_filming', 'I understand that, if selected, filming arrangements and any required permissions or releases will be coordinated before filming.']].map(([k, l]) => (
                        <label key={k} className="flex items-start gap-3 cursor-pointer">
                          <input type="checkbox" className="h-4 w-4 mt-0.5" checked={form[k]} onChange={setCheck(k)} data-testid={`sb-${k.replace(/_/g, '-')}`} />
                          <span className="text-sm text-slate-700">{l} <span className="text-rose-600" aria-hidden="true">*</span></span>
                        </label>
                      ))}
                      <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-slate-100">
                        <input type="checkbox" className="h-4 w-4 mt-0.5" checked={form.keep_on_file} onChange={setCheck('keep_on_file')} data-testid="sb-keep-on-file" />
                        <span className="text-sm text-slate-600">Keep my program's information on file for similar future opportunities.</span>
                      </label>
                      {errors.acks && <p className="text-xs text-rose-600" role="alert">{errors.acks}</p>}
                    </div>
                    <div className="mt-6 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg px-4 py-3">
                      <p className="text-sm text-amber-900">Please do not include children's names, photographs, or other sensitive personal information in this application.</p>
                    </div>
                    {submitError && (
                      <div className="mt-4 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" role="alert" data-testid="sb-submit-error">
                        <p className="text-sm text-rose-700 font-medium">{submitError}</p>
                        <p className="text-xs text-rose-600 mt-1">Your answers are still here — please review and press "Submit My Application" to try again.</p>
                      </div>
                    )}
                    <button type="submit" disabled={submitting} data-testid="sb-submit-btn"
                      className="mt-6 inline-flex items-center gap-2 px-8 py-4 bg-teal-700 hover:bg-teal-800 text-white font-bold rounded-full text-base transition-colors disabled:opacity-60 focus:outline-none focus:ring-4 focus:ring-teal-300/60">
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> : null}
                      {submitting ? 'Submitting…' : 'Submit My Application'}
                    </button>
                  </SectionCard>
                </form>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
